// Reads top-window scope so the editor chrome and canvas iframe share
// the same source (DOMQL clones context.globalScope per create()).
import { resolveTopWindow } from '../globalScope.js'

export function getCanvasScope(childKey) {
  const topWindow = resolveTopWindow()
  const scope = topWindow?.__canvasGlobalScope || this.context?.globalScope
  return childKey ? scope?.[childKey] : scope
}

export function getCanvasPositions() {
  const scope = this.call('getCanvasScope')
  return scope?.positions || { x: 0, y: 0, scale: 0.35 }
}

// Zoom methods. When the canvas v2 surface is active these branch off
// to the v2 viewport pipeline so the existing outer chrome (shared
// Zoombar, command bar, etc.) drives v2 transparently.

function v2ZoomAroundCenter(el, factor) {
  const v2 = el.call('getCanvasV2'); if (!v2) return
  const surf = el.getRoot?.()?.lookdown?.('CanvasSurface')
  const rect = surf?.node?.getBoundingClientRect()
  if (!rect) return
  const cx = rect.width / 2
  const cy = rect.height / 2
  const sv = v2.viewport
  const newScale = Math.max(0.035, Math.min(2.5, sv.scale * factor))
  const worldX = (cx - sv.x) / sv.scale
  const worldY = (cy - sv.y) / sv.scale
  el.call('applyViewport', { x: cx - worldX * newScale, y: cy - worldY * newScale, scale: newScale })
}

export function zoomToNode(targetNode, opts = {}) {
  const scope = this.call('getCanvasScope')
  if (!scope?.panzoomInstance || !targetNode) return
  const panzoom = scope.panzoomInstance

  if (opts.useZoom) {
    const { x, y } = panzoom.getTransform()
    panzoom.zoomAbs(x, y, opts.zoom || 0.65)
  }

  setTimeout(() => {
    const node = targetNode.node || targetNode
    const { x, y, width, height } = node.getBoundingClientRect()
    const panToX = x - (window.innerWidth - width) / 2
    const panToY = y - (window.innerHeight - height) / 2
    panzoom.moveBy(-panToX, -panToY, true)
  }, 50)
}

export function zoomIn() {
  if (this.call('isCanvasV2')) return v2ZoomAroundCenter(this, 1.25)
  const scope = this.call('getCanvasScope')
  if (!scope?.panzoomInstance) return
  const { x, y } = scope.positions
  scope.panzoomInstance.smoothZoom(
    x + window.innerWidth / 2,
    y + window.innerHeight / 2,
    1.25
  )
}

export function zoomOut() {
  if (this.call('isCanvasV2')) return v2ZoomAroundCenter(this, 0.8)
  const scope = this.call('getCanvasScope')
  if (!scope?.panzoomInstance) return
  const { x, y } = scope.positions
  scope.panzoomInstance.smoothZoom(
    x + window.innerWidth / 2,
    y + window.innerHeight / 2,
    0.75
  )
}

export function zoomReset() {
  if (this.call('isCanvasV2')) return this.call('resetCanvasView')
  const scope = this.call('getCanvasScope')
  if (!scope?.panzoomInstance) return
  const { x, y } = scope.positions
  scope.panzoomInstance.smoothZoomAbs(
    x + window.innerWidth / 2,
    y + window.innerHeight / 2,
    1
  )
}

export function getZoomLevel() {
  if (this.call('isCanvasV2')) return this.call('getCanvasV2')?.viewport?.scale || 0.35
  const scope = this.call('getCanvasScope')
  return scope?.positions?.scale || 0.35
}

// Navigation
export function findBoxByRoute(route) {
  const root = this.__ref?.root || this
  const collection = root?.CanvasScene?.Collection
  if (!collection?.node) return null
  const key = route === '/' ? '/' : '/' + route.split('/').filter(Boolean)[0]
  const children = collection.node.children
  for (let i = 0; i < children.length; i++) {
    if (children[i].ref?.scope?.key === key) return children[i].ref
  }
  return null
}

export function navigateCanvas(path) {
  const box = this.call('findBoxByRoute', path)
  if (box) this.call('zoomToNode', box)
}

// Selection / Hover
export function activateHovered(key) {
  const scope = this.call('getCanvasScope')
  if (!scope) return
  if (key === null) {
    scope.hovered = null
    scope.hoveredKey = null
  } else {
    scope.hovered = this
    scope.hoveredKey = typeof key === 'string' ? key : this.state?.key
  }
}

export function activateSelected(key) {
  if (this.call('isCanvasV2')) {
    if (key === null) this.call('clearSelection')
    else this.call('selectOne', typeof key === 'string' ? key : this.state?.key)
    return
  }
  const scope = this.call('getCanvasScope')
  if (!scope) return
  if (key === null) {
    scope.selected = null
    scope.selectedKey = null
  } else {
    scope.selected = this
    scope.selectedKey = typeof key === 'string' ? key : this.state?.key
  }
}

export function getSelected() {
  if (this.call('isCanvasV2')) {
    const key = this.call('getCanvasV2')?.selection?.primary
    if (!key) return null
    const items = this.getRoot?.()?.lookdown?.('CvItems')
    if (!items) return null
    for (const child of Object.values(items)) {
      if (child?.state?.key === key) return child
    }
    return null
  }
  const scope = this.call('getCanvasScope')
  return scope?.selected
}

export function getSelectedKey() {
  if (this.call('isCanvasV2')) return this.call('getCanvasV2')?.selection?.primary || null
  const scope = this.call('getCanvasScope')
  return scope?.selectedKey
}

export function getHoveredKey() {
  const scope = this.call('getCanvasScope')
  return scope?.hoveredKey
}

// The shared chrome speaks the v1 mode names (inspectMode / panningMode
// / defaultMode). When v2 is active we map each to the v2 mode and also
// mirror the v1 flags on scope so any polling consumer stays in sync.
const V1_TO_V2 = { inspectMode: 'inspect', panningMode: 'pan', defaultMode: 'default', interactMode: 'interact' }

export function isCanvasMode(mode) {
  if (this.call('isCanvasV2')) {
    const target = V1_TO_V2[mode]
    if (!target) return false
    return this.call('getCanvasV2')?.mode === target
  }
  const scope = this.call('getCanvasScope')
  return scope?.[mode] || false
}

export function setCanvasMode(mode) {
  if (this.call('isCanvasV2')) {
    const target = V1_TO_V2[mode] || mode
    this.call('setCanvasV2Mode', target)
    return
  }
  const scope = this.call('getCanvasScope')
  if (!scope) return
  scope.defaultMode = mode === 'defaultMode'
  scope.inspectMode = mode === 'inspectMode'
  scope.panningMode = mode === 'panningMode'
}

// `openModal('/settings')` from shared Zoombar/CommandBar opens the v2
// canvas settings instead of the missing routes-based modal.
export function openModal(route) {
  if (this.call('isCanvasV2') && route === '/settings') {
    this.call('toggleCanvasSettings')
    return
  }
}

// Bridges for shared CommandBar / SearchBar / Zoombar that aren't
// otherwise wired in v2. Each routes the v1 verb to its v2 equivalent.

// Zoombar keyboard icon → v2 shortcuts overlay. Falls back to no-op so
// non-v2 consumers don't break.
export function openKeyboardShortcuts() {
  if (this.call('isCanvasV2')) this.call('toggleShortcutsOverlay')
}

// `/open key` and SearchBar item click — explorer-window verb in v1.
// In v2, focus the artboard for that key.
export function setWindow(window, arg) {
  if (this.call('isCanvasV2')) {
    if (window === 'explorer' && arg && typeof arg === 'string') {
      // `arg` may be a page/component key or `'search:<query>'`.
      if (arg === 'onboarding') {
        this.call('toggleShortcutsOverlay')
        return
      }
      if (arg.startsWith('search:')) return
      this.call('selectOne', arg)
      this.call('fitToSelection')
      this.call('clearSelection')
    }
  }
}

// SearchBar/CommandBar item open verbs.
export function openArtboardItem(type, key) {
  if (this.call('isCanvasV2') && key) {
    this.call('selectOne', key)
    this.call('fitToSelection')
  }
}

export function openItem(type, key) { this.call('openArtboardItem', type, key) }
export function activateChosen(key, type) { this.call('openArtboardItem', type, key) }
export function activateFunction(_key) { /* no-op in v2 canvas */ }
export function setSidebarContent(_kind) {
  if (this.call('isCanvasV2')) this.call('toggleCanvasSettings')
}

// Theme toggle stub — flips data-theme on the canvas root document.
export function toggleTheme(target) {
  const doc = this.context?.document || document
  const cur = doc.documentElement.getAttribute('data-theme') || 'dark'
  const next = target || (cur === 'dark' ? 'light' : 'dark')
  doc.documentElement.setAttribute('data-theme', next)
}

// Generic cookie helper — Zoombar color picker uses this. Cheap impl;
// the editor likely has a real one elsewhere but local fallback keeps
// click-to-set working.
export function setCookie(name, value) {
  try {
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=/; max-age=31536000`
  } catch {}
}

export function syncCanvas() {
  // Trigger re-render of overlay by bumping a counter
  const scope = this.call('getCanvasScope')
  if (scope) scope._syncCounter = (scope._syncCounter || 0) + 1
}

export function getCanvasCollection() {
  const root = this?.getRoot?.()
  const scene = root?.lookdown?.((el) => el.key === 'CanvasScene')
  return scene?.Collection || scene
}

export function isReadOnly() {
  return false
}

export function isEditMode() {
  return false
}

export function isEmbed() {
  return false
}

export function isPlatform() {
  return false
}

export function getChosen() {
  return null
}

export function getChosenKey() {
  return null
}

export function getInContextKey() {
  return null
}

export function activateContext() {}
export function openEditor() {}

export function getRootState(key) {
  const root = this?.state?.root
  return key ? root?.[key] : root
}

export function getRootContext(key) {
  const ctx = this?.context
  return key ? ctx?.[key] : ctx
}
